import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Lock, KeyRound, Trash2, Check, Plus, Copy, Eye, X, Star, Sparkles,
  ArrowUpRight, Wallet, TrendingUp, LineChart, Landmark, Coins, ShieldCheck,
  UserCheck, UserX, Zap, Activity, FileText, IdCard, Camera, Upload, RefreshCw,
} from "lucide-react";
import { useSession } from "@/context/SessionContext";
import { vaultSecurityApi, streamGovImage } from "@/api/vaultSecurity";
import { api } from "@/api/client";
import FinancialLockGate from "@/components/dashboard/FinancialLockGate";

const FOUNDER_EMAIL = "djohnm1972@gmail.com";
const FOUNDER_HANDLE = "johnnywalker";
export function useFounder() {
  const { user } = useSession();
  return (
    user?.role === "founder" ||
    (user?.handle || "").toLowerCase() === FOUNDER_HANDLE ||
    (user?.email || "").toLowerCase() === FOUNDER_EMAIL
  );
}

const CLS = { business: "Business", private: "Private" };

/* Small reusable card shell matching the vault neobrutalist language. */
export function VaultCard({ icon: Icon, title, sub, testid, children, right }) {
  return (
    <div className="border-2 border-gold/50 rounded-2xl bg-white/5 p-3.5 shadow-[3px_3px_0px_#000]" data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className="w-9 h-9 rounded-xl border-2 border-gold bg-white/10 grid place-items-center shrink-0"><Icon size={16} className="text-gold" /></span>
        <div className="flex-1 leading-tight">
          <p className="font-serif text-lg text-white">{title}</p>
          <p className="font-mono text-[8px] uppercase tracking-widest text-gold/70">{sub}</p>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

/* Business / Private classification toggle (defaults Private). */
function ClassToggle({ value, onChange, testid }) {
  return (
    <div className="flex gap-2" data-testid={testid}>
      {Object.keys(CLS).map((k) => (
        <button key={k} onClick={() => onChange(value === k ? "private" : k)} data-testid={`${testid}-${k}`}
          className={`flex-1 flex items-center justify-center gap-1 rounded-lg border-2 py-1.5 font-mono text-[9px] font-black uppercase tracking-widest ${value === k ? "bg-gold text-black border-charcoal" : "bg-white/5 text-white/50 border-gold/30"}`}>
          <span className="w-3 h-3 border-2 border-current grid place-items-center rounded-[3px]">{value === k && <Check size={8} strokeWidth={4} />}</span>{CLS[k]}
        </button>
      ))}
    </div>
  );
}

/* ============ C1 — Upgrade-gate CTA inside every LockedRow ============ */
const TIER_RANK = { tier_1: 1, tier_2: 2, tier_3: 3 };
const TIER_LABEL = { tier_1: "MEMBER", tier_2: "CREATOR", tier_3: "BUSINESS" };
export function UpgradeLockedRow({ rowKey, title, min, onUpgrade }) {
  const need = Object.keys(TIER_RANK).find((k) => TIER_RANK[k] === min) || "tier_2";
  return (
    <div className="border-2 border-gold/20 rounded-2xl bg-white/[0.03] p-3.5" data-testid={`vault-row-${rowKey}`}>
      <div className="flex items-center gap-2">
        <span className="w-9 h-9 rounded-xl border-2 border-gold/30 bg-white/5 grid place-items-center shrink-0"><Lock size={15} className="text-gold/50" /></span>
        <div className="flex-1 leading-tight">
          <p className="font-serif text-lg text-white/50">{title}</p>
          <p className="font-mono text-[8px] uppercase tracking-widest text-gold/40">Locked · {TIER_LABEL[need]} package</p>
        </div>
        <span data-testid={`row-lock-${rowKey}`} className="inline-flex items-center gap-1 rounded-full border-2 border-gold/40 bg-gold/10 px-2 py-0.5 font-mono text-[8px] font-black uppercase text-gold">
          <Star size={9} /> {TIER_LABEL[need]}
        </span>
      </div>
      <button onClick={() => onUpgrade?.(need)} data-testid={`upgrade-cta-${rowKey}`}
        className="mt-3 w-full flex items-center justify-center gap-1.5 bg-rust text-white font-mono text-[10px] font-black uppercase tracking-widest py-2.5 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
        <ArrowUpRight size={13} /> Upgrade to {TIER_LABEL[need]}
      </button>
    </div>
  );
}

/* ============ C2 — Extensible masked BYOK key-value registry ============ */
export function ByokRegistry() {
  const [keys, setKeys] = useState(null);
  const [editing, setEditing] = useState(null);
  const [val, setVal] = useState("");
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");

  const load = () => vaultSecurityApi.byokRegistry().then(setKeys).catch(() => setKeys([]));
  useEffect(() => { load(); }, []);

  const save = async (name) => {
    if (val.trim().length < 4) { toast.error("Value too short"); return; }
    try { setKeys(await vaultSecurityApi.byokRegSet(name, val.trim())); setEditing(null); setVal(""); toast.success(`${name} stored (masked)`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const wipe = async (name) => { setKeys(await vaultSecurityApi.byokRegWipe(name)); toast(`${name} wiped`); };
  const copy = async (name) => {
    try { const raw = await vaultSecurityApi.byokRegReveal(name); await navigator.clipboard?.writeText(raw); toast.success("Copied to clipboard (write-only)"); }
    catch { toast.error("Nothing to copy"); }
  };
  const addCustom = async () => {
    if (val.trim().length < 4) { toast.error("Value too short"); return; }
    // Auto-default the name behind the scenes when the user skips the nickname.
    const keyName = newName.trim() || `MY_CONNECTION_${keys.length + 1}`;
    try { setKeys(await vaultSecurityApi.byokRegSet(keyName, val.trim())); setAdding(false); setNewName(""); setVal(""); toast.success("Connection saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <VaultCard icon={KeyRound} title="BYOK Registry" sub="Row 2 · schema-agnostic keys · masked" testid="vault-row-byok">
      <div className="mt-3 space-y-2">
        {(keys || []).map((k) => (
          <div key={k.name} className="flex items-center gap-2" data-testid={`byokreg-row-${k.name}`}>
            <span className="font-mono text-[9px] font-black uppercase tracking-wide text-white w-[92px] shrink-0 truncate" title={k.name}>{k.name.replace(/_API_KEY$/, "").replace(/_/g, " ")}</span>
            {editing === k.name ? (
              <>
                <input autoFocus type="password" value={val} onChange={(e) => setVal(e.target.value)} data-testid={`byokreg-input-${k.name}`} placeholder="paste key…"
                  className="flex-1 min-w-0 bg-white/10 border-2 border-gold rounded-lg px-2 py-1.5 font-mono text-[10px] text-white outline-none focus:ring-2 focus:ring-gold" />
                <button onClick={() => save(k.name)} data-testid={`byokreg-save-${k.name}`} className="w-8 h-8 grid place-items-center rounded-lg bg-gold border-2 border-charcoal shrink-0"><Check size={14} className="text-charcoal" /></button>
              </>
            ) : (
              <>
                <button onClick={() => { setEditing(k.name); setVal(""); }} data-testid={`byokreg-edit-${k.name}`}
                  className="flex-1 min-w-0 text-left bg-white/5 border-2 border-gold/40 rounded-lg px-2 py-1.5 font-mono text-[10px] text-white/70 truncate">
                  {k.set ? (k.masked || "••••••••") : "tap to add key"}
                </button>
                {k.set && <>
                  <button onClick={() => copy(k.name)} data-testid={`byokreg-copy-${k.name}`} className="w-8 h-8 grid place-items-center rounded-lg bg-white/10 border-2 border-gold/50 shrink-0"><Copy size={13} className="text-gold" /></button>
                  <button onClick={() => wipe(k.name)} data-testid={`byokreg-wipe-${k.name}`} className="w-8 h-8 grid place-items-center rounded-lg bg-red-600/80 border-2 border-charcoal shrink-0"><Trash2 size={13} className="text-white" /></button>
                </>}
              </>
            )}
          </div>
        ))}
      </div>
      {adding ? (
        <div className="mt-2 space-y-2 border-2 border-dashed border-gold/40 rounded-xl p-2" data-testid="byokreg-add-form">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="byokreg-new-name" placeholder="Give this connection a nickname (optional)"
            className="w-full bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-1.5 font-mono text-[10px] text-white outline-none focus:ring-2 focus:ring-gold" />
          <input type="password" value={val} onChange={(e) => setVal(e.target.value)} data-testid="byokreg-new-value" placeholder="paste value…"
            className="w-full bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-1.5 font-mono text-[10px] text-white outline-none focus:ring-2 focus:ring-gold" />
          <div className="flex gap-2">
            <button onClick={addCustom} data-testid="byokreg-add-save" className="flex-1 bg-gold text-black font-mono text-[9px] font-black uppercase tracking-widest py-2 rounded-full border-2 border-charcoal">Save Variable</button>
            <button onClick={() => { setAdding(false); setNewName(""); setVal(""); }} data-testid="byokreg-add-cancel" className="px-3 bg-white/10 text-white/60 border-2 border-cream/30 rounded-full font-mono text-[9px] uppercase">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => { setAdding(true); setVal(""); }} data-testid="byokreg-add-btn"
          className="mt-2 w-full flex items-center justify-center gap-1.5 border-2 border-dashed border-gold/50 rounded-xl py-2.5 font-mono text-[10px] font-black uppercase tracking-widest text-gold active:scale-[0.99]">
          <Plus size={13} /> Add Custom Variable
        </button>
      )}
    </VaultCard>
  );
}

/* ============ C3 — Founder cash-flow ledger + inbox scraper ============ */
export function CashFlowLedger() {
  const founder = useFounder();
  const [data, setData] = useState(null);
  const [creds, setCreds] = useState({ client_id: "", secret_key: "", access_token: "" });
  const [showCreds, setShowCreds] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => vaultSecurityApi.cashflow().then(setData).catch(() => setData({ locked: true }));
  useEffect(() => { load(); }, []);

  const saveCreds = async () => {
    if (!creds.client_id || !creds.secret_key || !creds.access_token) { toast.error("All three fields required"); return; }
    setBusy(true);
    try { await vaultSecurityApi.cashflowCreds(creds); setShowCreds(false); await load(); toast.success("Ledger credentials linked"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  if (!founder || data?.locked) {
    return (
      <VaultCard icon={Landmark} title="Cash-Flow Ledger" sub="live ARR · MRR · in/out" testid="vault-row-cashflow">
        <FinancialLockGate testid="cashflow-lock" />
      </VaultCard>
    );
  }

  const l = data?.ledger || {};
  return (
    <VaultCard icon={Landmark} title="Cash-Flow Ledger" sub="live ARR · MRR · in/out" testid="vault-row-cashflow">
      <div className="mt-3 grid grid-cols-2 gap-2" data-testid="cashflow-metrics">
        {[["ARR", l.arr], ["MRR", l.mrr], ["Inflows", l.inflows], ["Outflows", l.outflows]].map(([k, v]) => (
          <div key={k} className="border-2 border-gold/40 rounded-xl bg-white/5 p-2">
            <p className="font-mono text-[8px] uppercase tracking-widest text-gold/70">{k}</p>
            <p className="font-serif text-xl text-white tabular-nums">{v == null ? "—" : Math.round(v).toLocaleString()}</p>
          </div>
        ))}
      </div>
      {showCreds ? (
        <div className="mt-2 space-y-2 border-2 border-dashed border-gold/40 rounded-xl p-2" data-testid="cashflow-creds-form">
          {[["client_id", "CLIENT_ID"], ["secret_key", "SECRET_KEY"], ["access_token", "ACCESS_TOKEN"]].map(([k, ph]) => (
            <input key={k} type={k === "client_id" ? "text" : "password"} value={creds[k]} onChange={(e) => setCreds((c) => ({ ...c, [k]: e.target.value }))} placeholder={ph} data-testid={`cashflow-${k}`}
              className="w-full bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-1.5 font-mono text-[10px] text-white outline-none focus:ring-2 focus:ring-gold" />
          ))}
          <button onClick={saveCreds} disabled={busy} data-testid="cashflow-creds-save" className="w-full bg-gold text-black font-mono text-[9px] font-black uppercase tracking-widest py-2 rounded-full border-2 border-charcoal disabled:opacity-60">{busy ? "Linking…" : "Link Live Ledger"}</button>
        </div>
      ) : (
        <button onClick={() => setShowCreds(true)} data-testid="cashflow-creds-btn"
          className="mt-2 w-full flex items-center justify-center gap-1.5 border-2 border-gold/50 rounded-full py-2 font-mono text-[9px] font-black uppercase tracking-widest text-gold">
          <KeyRound size={12} /> {data?.creds_set ? "Update Ledger Credentials" : "Connect Live Ledger"}
        </button>
      )}
    </VaultCard>
  );
}

/* ============ C5 — Multi-asset canvas (Web3 + stocks + net worth) ======= */
export function AssetCanvas() {
  const founder = useFounder();
  const [web3, setWeb3] = useState(null);
  const [stocks, setStocks] = useState(null);
  const [canvas, setCanvas] = useState(null);
  const [w3, setW3] = useState({ address: "", wallet_source: "", email: "", classification: "private" });
  const [sk, setSk] = useState({ ticker: "", shares: "", cost_basis: "", brokerage: "", brokerage_email: "", classification: "private" });
  const [scans, setScans] = useState({});
  const [scanning, setScanning] = useState("");

  const scanWallet = async (id) => {
    setScanning(id);
    try {
      const r = await vaultSecurityApi.web3ScanStored(id);
      setScans((s) => ({ ...s, [id]: r.scan }));
      if (r.scan?.live) { toast.success(`Live · $${r.scan.total_usd} · ${r.scan.token_count} coins`); loadCanvas(); }
      else toast.error(r.scan?.error || "Scan failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
    finally { setScanning(""); }
  };

  const loadCanvas = () => vaultSecurityApi.networthCanvas().then(setCanvas).catch(() => setCanvas(null));
  const load = () => {
    vaultSecurityApi.web3List().then(setWeb3).catch(() => setWeb3([]));
    vaultSecurityApi.stockList().then(setStocks).catch(() => setStocks([]));
    loadCanvas();
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const addWeb3 = async () => {
    if (w3.address.trim().length < 4) { toast.error("Enter a wallet address"); return; }
    try { await vaultSecurityApi.web3Add(w3); setW3({ address: "", wallet_source: "", email: "", classification: "private" }); load(); toast.success("Wallet tracked"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const addStock = async () => {
    if (!sk.ticker.trim() || !sk.shares) { toast.error("Ticker + shares required"); return; }
    try { await vaultSecurityApi.stockAdd({ ...sk, shares: Number(sk.shares), cost_basis: Number(sk.cost_basis || 0) }); setSk({ ticker: "", shares: "", cost_basis: "", brokerage: "", brokerage_email: "", classification: "private" }); load(); toast.success("Position tracked"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const series = canvas?.series || [];
  const max = Math.max(...series.map((p) => Math.abs(Number(p.v) || 0)), 1);
  const bd = canvas?.breakdown || {};

  if (!founder) {
    return (
      <VaultCard icon={LineChart} title="Asset Canvas" sub="Row 6 · web3 · stocks · net worth" testid="vault-row-assets">
        <FinancialLockGate testid="assets-lock" />
      </VaultCard>
    );
  }

  return (
    <VaultCard icon={LineChart} title="Asset Canvas" sub="Row 6 · web3 · stocks · net worth" testid="vault-row-assets">
      {/* Net-worth graph */}
      <div className="mt-3 border-2 border-gold/40 rounded-xl bg-white/5 p-2.5" data-testid="networth-canvas">
        <div className="flex items-center justify-between mb-1.5">
          <p className="font-mono text-[9px] uppercase tracking-widest text-gold/80 flex items-center gap-1"><TrendingUp size={11} /> Net Worth</p>
          <span className="font-serif text-lg text-white tabular-nums" data-testid="networth-value">{canvas ? Math.round(canvas?.summary?.net_worth || 0).toLocaleString() : "—"}</span>
        </div>
        {series.length ? (
          <div className="flex items-end gap-1 h-20" data-testid="networth-series">
            {series.slice(-14).map((p, i) => (
              <div key={i} title={`${p.t}: ${p.v}`} className={`flex-1 rounded-t ${p.t === "NET" ? "bg-rust" : "bg-gold/80"}`} style={{ height: `${Math.max(6, (Math.abs(Number(p.v) || 0) / max) * 74)}%` }} />
            ))}
          </div>
        ) : <p className="font-mono text-[9px] text-white/40 py-2 text-center">No vectors yet.</p>}
        {founder && (
          <div className="mt-2 flex justify-between font-mono text-[8px] uppercase tracking-widest text-white/50" data-testid="networth-breakdown">
            <span><Landmark size={9} className="inline text-gold" /> Bank {Math.round(bd.bank || 0)}</span>
            <span><Coins size={9} className="inline text-gold" /> Web3 {Math.round(bd.web3 || 0)}</span>
            <span><LineChart size={9} className="inline text-gold" /> Stocks {Math.round(bd.stocks || 0)}</span>
          </div>
        )}
      </div>

      {/* Web3 module — LIVE keyless Solana mainnet scraper */}
      <div className="mt-3" data-testid="web3-module">
        <p className="font-mono text-[9px] uppercase tracking-widest text-gold/80 mb-1.5 flex items-center gap-1"><Wallet size={11} /> Web3 & Meme Coins · Live Solana</p>
        <div className="space-y-1.5 mb-2">
          {(web3 || []).map((a) => {
            const sc = scans[a.id];
            return (
            <div key={a.id} className="border-2 border-gold/30 rounded-lg bg-white/5 px-2 py-1.5" data-testid={`web3-item-${a.id}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[9px] text-white/80 flex-1 truncate">{a.address_masked} · {a.wallet_source || "wallet"}</span>
                <span className={`font-mono text-[7px] uppercase px-1.5 rounded-full border ${a.classification === "business" ? "border-rust text-rust" : "border-gold/40 text-gold/70"}`}>{a.classification === "business" ? "Biz" : "Personal"}</span>
                <button onClick={() => scanWallet(a.id)} disabled={scanning === a.id} data-testid={`web3-scan-${a.id}`} className="w-6 h-6 grid place-items-center rounded bg-gold/90 border border-charcoal" aria-label="Scan live">
                  {scanning === a.id ? <Loader2 size={11} className="text-charcoal animate-spin" /> : <RefreshCw size={11} className="text-charcoal" strokeWidth={3} />}
                </button>
                <button onClick={async () => { await vaultSecurityApi.web3Delete(a.id); load(); }} data-testid={`web3-del-${a.id}`} className="w-6 h-6 grid place-items-center rounded bg-red-600/80 border border-charcoal"><X size={11} className="text-white" strokeWidth={3} /></button>
              </div>
              {sc?.live && (
                <div className="mt-1.5 border-t border-gold/20 pt-1.5" data-testid={`web3-live-${a.id}`}>
                  <div className="flex items-center justify-between font-mono text-[9px]">
                    <span className="text-white/70">◎ {sc.sol.balance} SOL</span>
                    <span className="text-gold font-black">${sc.total_usd.toLocaleString()}</span>
                  </div>
                  {(sc.tokens || []).slice(0, 3).map((t) => (
                    <div key={t.mint} className="flex items-center justify-between font-mono text-[8px] mt-0.5">
                      <span className="text-white/60 truncate max-w-[45%]">{t.symbol}</span>
                      <span className="text-white/50">${t.value_usd}</span>
                      <span className={t.change_24h >= 0 ? "text-[#9EFFB8]" : "text-red-400"}>{t.change_24h >= 0 ? "▲" : "▼"} {Math.abs(t.change_24h)}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ); })}
        </div>
        <div className="space-y-1.5 border-2 border-dashed border-gold/40 rounded-xl p-2">
          <input value={w3.address} onChange={(e) => setW3((s) => ({ ...s, address: e.target.value }))} placeholder="Phantom / Solana Public Address" data-testid="web3-address" className="w-full bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-1.5 font-mono text-[10px] text-white outline-none focus:ring-2 focus:ring-gold" />
          <div className="grid grid-cols-2 gap-1.5">
            <input value={w3.wallet_source} onChange={(e) => setW3((s) => ({ ...s, wallet_source: e.target.value }))} placeholder="Source (Phantom…)" data-testid="web3-source" className="bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-1.5 font-mono text-[10px] text-white outline-none focus:ring-2 focus:ring-gold" />
            <input value={w3.email} onChange={(e) => setW3((s) => ({ ...s, email: e.target.value }))} placeholder="Email (opt)" data-testid="web3-email" className="bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-1.5 font-mono text-[10px] text-white outline-none focus:ring-2 focus:ring-gold" />
          </div>
          <ClassToggle value={w3.classification} onChange={(v) => setW3((s) => ({ ...s, classification: v }))} testid="web3-class" />
          <button onClick={addWeb3} data-testid="web3-add" className="w-full bg-gold text-black font-mono text-[9px] font-black uppercase tracking-widest py-2 rounded-full border-2 border-charcoal">Track Wallet</button>
        </div>
      </div>

      {/* Stocks module */}
      <div className="mt-3" data-testid="stock-module">
        <p className="font-mono text-[9px] uppercase tracking-widest text-gold/80 mb-1.5 flex items-center gap-1"><LineChart size={11} /> Corporate Shares & Stocks</p>
        <div className="space-y-1.5 mb-2">
          {(stocks || []).map((a) => (
            <div key={a.id} className="flex items-center gap-2 border-2 border-gold/30 rounded-lg bg-white/5 px-2 py-1.5" data-testid={`stock-item-${a.id}`}>
              <span className="font-mono text-[9px] text-white/80 flex-1 truncate">{a.ticker} · {a.shares} sh · {a.brokerage || "—"}</span>
              <span className="font-mono text-[9px] text-gold">${Math.round(a.equity)}</span>
              <button onClick={async () => { await vaultSecurityApi.stockDelete(a.id); load(); }} data-testid={`stock-del-${a.id}`} className="w-6 h-6 grid place-items-center rounded bg-red-600/80 border border-charcoal"><X size={11} className="text-white" strokeWidth={3} /></button>
            </div>
          ))}
        </div>
        <div className="space-y-1.5 border-2 border-dashed border-gold/40 rounded-xl p-2">
          <div className="grid grid-cols-3 gap-1.5">
            <input value={sk.ticker} onChange={(e) => setSk((s) => ({ ...s, ticker: e.target.value }))} placeholder="Ticker" data-testid="stock-ticker" className="bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-1.5 font-mono text-[10px] uppercase text-white outline-none focus:ring-2 focus:ring-gold" />
            <input value={sk.shares} onChange={(e) => setSk((s) => ({ ...s, shares: e.target.value }))} inputMode="decimal" placeholder="Shares" data-testid="stock-shares" className="bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-1.5 font-mono text-[10px] text-white outline-none focus:ring-2 focus:ring-gold" />
            <input value={sk.cost_basis} onChange={(e) => setSk((s) => ({ ...s, cost_basis: e.target.value }))} inputMode="decimal" placeholder="Avg Cost" data-testid="stock-cost" className="bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-1.5 font-mono text-[10px] text-white outline-none focus:ring-2 focus:ring-gold" />
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <input value={sk.brokerage} onChange={(e) => setSk((s) => ({ ...s, brokerage: e.target.value }))} placeholder="Brokerage (Stake…)" data-testid="stock-brokerage" className="bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-1.5 font-mono text-[10px] text-white outline-none focus:ring-2 focus:ring-gold" />
            <input value={sk.brokerage_email} onChange={(e) => setSk((s) => ({ ...s, brokerage_email: e.target.value }))} placeholder="Notify Email" data-testid="stock-email" className="bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-1.5 font-mono text-[10px] text-white outline-none focus:ring-2 focus:ring-gold" />
          </div>
          <ClassToggle value={sk.classification} onChange={(v) => setSk((s) => ({ ...s, classification: v }))} testid="stock-class" />
          <button onClick={addStock} data-testid="stock-add" className="w-full bg-gold text-black font-mono text-[9px] font-black uppercase tracking-widest py-2 rounded-full border-2 border-charcoal">Track Position</button>
        </div>
      </div>
    </VaultCard>
  );
}

/* ============ C6 — Master Admin desk (FOUNDER ONLY) ============ */
export function AdminDesk() {
  const founder = useFounder();
  const [queue, setQueue] = useState(null);
  const [diag, setDiag] = useState(null);
  const [email, setEmail] = useState("");
  const [cls, setCls] = useState("business");

  const load = () => {
    vaultSecurityApi.adminQueue().then(setQueue).catch(() => setQueue([]));
    vaultSecurityApi.adminDiagnostics().then(setDiag).catch(() => setDiag(null));
  };
  useEffect(() => { if (founder) load(); }, [founder]);
  if (!founder) return null;

  const approve = async (uid) => { await vaultSecurityApi.adminApprove(uid); load(); toast.success("User approved"); };
  const reject = async (uid) => { await vaultSecurityApi.adminReject(uid); load(); toast("User rejected"); };
  const forceClear = async () => {
    if (!email.trim()) { toast.error("Enter an account email"); return; }
    try { await vaultSecurityApi.adminForceClear(email.trim()); setEmail(""); load(); toast.success("Unverified alert cleared"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Not found"); }
  };

  return (
    <VaultCard icon={ShieldCheck} title="Master Admin Desk" sub="founder · verification controller" testid="vault-row-admin"
      right={<span className="font-mono text-[8px] font-black uppercase text-rust border-2 border-rust/50 rounded-full px-2 py-0.5">FOUNDER</span>}>
      {/* Diagnostics */}
      <div className="mt-3 grid grid-cols-3 gap-1.5" data-testid="admin-diagnostics">
        {[["Users", diag?.users], ["Verified", diag?.verified_users], ["Pending", diag?.pending_verification], ["Reels", diag?.reels], ["Web3", diag?.web3_assets], ["Stocks", diag?.stock_assets]].map(([k, v]) => (
          <div key={k} className="border-2 border-gold/30 rounded-lg bg-white/5 p-1.5 text-center">
            <p className="font-serif text-lg text-white tabular-nums leading-none">{v ?? "—"}</p>
            <p className="font-mono text-[7px] uppercase tracking-widest text-gold/60 mt-0.5">{k}</p>
          </div>
        ))}
      </div>
      {diag?.clusters?.length ? (
        <p className="mt-1.5 font-mono text-[8px] uppercase tracking-widest text-white/40 flex items-center gap-1" data-testid="admin-clusters"><Activity size={9} className="text-gold" /> {diag.clusters.join(" · ")}</p>
      ) : null}

      {/* Identity queue */}
      <p className="mt-3 font-mono text-[9px] uppercase tracking-widest text-gold/80 mb-1.5">Identity Queue</p>
      <div className="space-y-1.5" data-testid="admin-queue">
        {queue == null ? <Loader2 size={14} className="text-gold animate-spin" />
          : queue.length === 0 ? <p className="font-mono text-[9px] text-white/40">Queue empty — all clear.</p>
          : queue.map((u) => (
            <div key={u.user_id} className="flex items-center gap-2 border-2 border-gold/30 rounded-lg bg-white/5 px-2 py-1.5" data-testid={`admin-queue-${u.user_id}`}>
              <span className="font-mono text-[9px] text-white/80 flex-1 truncate">{u.name || u.handle || u.user_id} · {u.email}</span>
              <button onClick={() => approve(u.user_id)} data-testid={`admin-approve-${u.user_id}`} className="w-7 h-7 grid place-items-center rounded bg-emerald-500 border-2 border-charcoal"><UserCheck size={13} className="text-white" /></button>
              <button onClick={() => reject(u.user_id)} data-testid={`admin-reject-${u.user_id}`} className="w-7 h-7 grid place-items-center rounded bg-red-600 border-2 border-charcoal animate-blink"><UserX size={13} className="text-white" /></button>
            </div>
          ))}
      </div>

      {/* Override matrix */}
      <p className="mt-3 font-mono text-[9px] uppercase tracking-widest text-gold/80 mb-1.5">Override Matrix</p>
      <div className="flex gap-2" data-testid="admin-override">
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="account email" data-testid="admin-override-email" className="flex-1 min-w-0 bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-2 font-mono text-[10px] text-white outline-none focus:ring-2 focus:ring-gold" />
        <button onClick={forceClear} data-testid="admin-force-clear" className="flex items-center gap-1 px-3 rounded-lg font-mono text-[9px] font-black uppercase tracking-widest text-white border-2 border-charcoal" style={{ backgroundColor: "#FF4D4D" }}><Zap size={12} /> Force Clear</button>
      </div>
      <div className="mt-2"><ClassToggle value={cls} onChange={setCls} testid="admin-class" /></div>
    </VaultCard>
  );
}

/* ============ C7 — Hidden Government Document Vault (FOUNDER) ============ */
export function GovDocVault({ onClose }) {
  const [docs, setDocs] = useState(null);
  const [form, setForm] = useState({ doc_type: "passport", label: "", number: "", country: "", expiry: "", classification: "private" });
  const [reveal, setReveal] = useState({}); // id -> plaintext (auto-expires)

  const load = () => vaultSecurityApi.govList().then(setDocs).catch(() => setDocs([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.number.trim()) { toast.error("Enter document number"); return; }
    try { await vaultSecurityApi.govAdd(form); setForm({ doc_type: "passport", label: "", number: "", country: "", expiry: "", classification: "private" }); load(); toast.success("Document sealed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doReveal = async (id) => {
    try {
      const r = await vaultSecurityApi.govReveal(id);
      setReveal((s) => ({ ...s, [id]: r.number }));
      toast(`Revealed for ${r.reveal_ttl}s`);
      setTimeout(() => setReveal((s) => { const n = { ...s }; delete n[id]; return n; }), (r.reveal_ttl || 10) * 1000);
    } catch { toast.error("Reveal failed"); }
  };
  const copy = async (id) => {
    try { const r = await vaultSecurityApi.govReveal(id); await navigator.clipboard?.writeText(r.number); toast.success("Copied (write-only)"); }
    catch { toast.error("Copy failed"); }
  };
  const wipe = async (id) => { await vaultSecurityApi.govWipe(id); load(); toast("Record purged"); };

  return (
    <div className="absolute inset-0 z-50 bg-black/90 flex items-end" data-testid="gov-doc-vault">
      <div className="w-full max-h-[92%] overflow-y-auto no-scrollbar bg-charcoal border-t-4 border-rust rounded-t-3xl p-5 space-y-3">
        <div className="flex items-center gap-2 pb-2 border-b-2 border-rust/30">
          <Lock size={16} className="text-rust" />
          <h3 className="font-serif text-xl text-white flex-1">Component 12 · Gov Documents</h3>
          <button onClick={onClose} data-testid="gov-close" className="w-8 h-8 grid place-items-center rounded-lg border-2 border-gold bg-white/10"><X size={15} className="text-gold" /></button>
        </div>
        <p className="font-mono text-[8px] uppercase tracking-widest text-rust">Hidden compartment · founder-only · masked at rest</p>

        {/* Existing sealed docs */}
        <div className="space-y-2" data-testid="gov-list">
          {docs == null ? <Loader2 size={14} className="text-gold animate-spin" />
            : docs.length === 0 ? <p className="font-mono text-[9px] text-white/40">No sealed documents.</p>
            : docs.map((d) => (
              <div key={d.id} className="border-2 border-gold/40 rounded-xl bg-white/5 p-2.5" data-testid={`gov-item-${d.id}`}>
                <div className="flex items-center gap-2">
                  <IdCard size={15} className="text-gold shrink-0" />
                  <span className="font-mono text-[10px] text-white/80 flex-1 truncate">{d.label || d.doc_type} · {d.country || "—"} {d.expiry && `· exp ${d.expiry}`}</span>
                  <span className="font-mono text-[8px] uppercase text-gold/70 border border-gold/40 rounded-full px-1.5">{d.classification}</span>
                </div>
                <div className="mt-1.5 flex items-center gap-2">
                  <span className="flex-1 font-mono text-[11px] text-gold tracking-wider truncate" data-testid={`gov-number-${d.id}`}>{reveal[d.id] || d.number_masked}</span>
                  <button onClick={() => doReveal(d.id)} data-testid={`gov-reveal-${d.id}`} className="w-7 h-7 grid place-items-center rounded bg-white/10 border-2 border-gold/50"><Eye size={12} className="text-gold" /></button>
                  <button onClick={() => copy(d.id)} data-testid={`gov-copy-${d.id}`} className="w-7 h-7 grid place-items-center rounded bg-white/10 border-2 border-gold/50"><Copy size={12} className="text-gold" /></button>
                  <button onClick={() => wipe(d.id)} data-testid={`gov-wipe-${d.id}`} className="w-7 h-7 grid place-items-center rounded bg-red-600/80 border-2 border-charcoal"><Trash2 size={12} className="text-white" /></button>
                </div>
                <GovImageRow doc={d} onDone={load} />
              </div>
            ))}
        </div>

        {/* New doc form */}
        <div className="space-y-2 border-2 border-dashed border-gold/40 rounded-xl p-2.5" data-testid="gov-add-form">
          <select value={form.doc_type} onChange={(e) => setForm((s) => ({ ...s, doc_type: e.target.value }))} data-testid="gov-doc-type" className="w-full bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-1.5 font-mono text-[10px] text-white outline-none">
            <option value="passport" className="bg-charcoal">Passport</option>
            <option value="license" className="bg-charcoal">Driver's License</option>
            <option value="custom" className="bg-charcoal">Custom</option>
          </select>
          <input value={form.label} onChange={(e) => setForm((s) => ({ ...s, label: e.target.value }))} placeholder="Label" data-testid="gov-label" className="w-full bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-1.5 font-mono text-[10px] text-white outline-none focus:ring-2 focus:ring-gold" />
          <input type="password" value={form.number} onChange={(e) => setForm((s) => ({ ...s, number: e.target.value }))} placeholder="Document Number" data-testid="gov-number" className="w-full bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-1.5 font-mono text-[10px] text-white outline-none focus:ring-2 focus:ring-gold" />
          <div className="grid grid-cols-2 gap-1.5">
            <input value={form.country} onChange={(e) => setForm((s) => ({ ...s, country: e.target.value }))} placeholder="Country" data-testid="gov-country" className="bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-1.5 font-mono text-[10px] text-white outline-none focus:ring-2 focus:ring-gold" />
            <input type="password" value={form.expiry} onChange={(e) => setForm((s) => ({ ...s, expiry: e.target.value }))} placeholder="Expiry" data-testid="gov-expiry" className="bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-1.5 font-mono text-[10px] text-white outline-none focus:ring-2 focus:ring-gold" />
          </div>
          <ClassToggle value={form.classification} onChange={(v) => setForm((s) => ({ ...s, classification: v }))} testid="gov-class" />
          <button onClick={add} data-testid="gov-add" className="w-full bg-black text-gold font-mono text-[10px] font-black uppercase tracking-widest py-3 rounded-xl border-2 border-gold flex items-center justify-center gap-2"><Lock size={13} /> Seal Document</button>
        </div>
      </div>
    </div>
  );
}

function GovImageRow({ doc, onDone }) {
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (side, e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setBusy(true);
    try { await vaultSecurityApi.govUploadImage(doc.id, side, file); toast.success(`${side} image sealed`); onDone?.(); }
    catch (e2) { toast.error(e2?.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 grid grid-cols-2 gap-1.5">
      <input ref={frontRef} type="file" accept="image/*" hidden onChange={(e) => upload("front", e)} data-testid={`gov-front-input-${doc.id}`} />
      <input ref={backRef} type="file" accept="image/*" hidden onChange={(e) => upload("back", e)} data-testid={`gov-back-input-${doc.id}`} />
      <button onClick={() => frontRef.current?.click()} disabled={busy} data-testid={`gov-front-${doc.id}`} className="flex items-center justify-center gap-1 border-2 border-gold/40 rounded-lg py-1.5 font-mono text-[8px] uppercase tracking-widest text-gold">{doc.front_id ? <Check size={11} /> : <Camera size={11} />} Front</button>
      <button onClick={() => backRef.current?.click()} disabled={busy} data-testid={`gov-back-${doc.id}`} className="flex items-center justify-center gap-1 border-2 border-gold/40 rounded-lg py-1.5 font-mono text-[8px] uppercase tracking-widest text-gold">{doc.back_id ? <Check size={11} /> : <Upload size={11} />} Back</button>
    </div>
  );
}

/* Step-up challenge before mounting Component 12 (PIN re-entry). */
export function GovStepUp({ onPass, onClose }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await vaultSecurityApi.govAccess();               // founder identity gate
      await api.post("/auth/verify-pin", { pin });       // PIN step-up
      onPass();
    } catch (e) { toast.error(e?.response?.data?.detail || "Step-up failed"); setPin(""); }
    finally { setBusy(false); }
  };
  return (
    <div className="absolute inset-0 z-50 bg-black/90 grid place-items-center px-8" data-testid="gov-stepup">
      <div className="w-full max-w-[300px] bg-charcoal border-4 border-rust rounded-3xl p-5 text-center space-y-4">
        <Lock size={28} className="text-rust mx-auto" />
        <p className="font-mono text-[10px] uppercase tracking-widest text-white/60">Founder step-up · re-enter PIN to open the hidden compartment</p>
        <input type="password" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 12))} onKeyDown={(e) => e.key === "Enter" && submit()} inputMode="numeric" autoFocus data-testid="gov-stepup-pin" className="w-full bg-white/10 border-2 border-gold rounded-xl px-4 py-3 font-mono text-lg tracking-[0.4em] text-white text-center outline-none focus:ring-2 focus:ring-gold" placeholder="••••••" />
        <div className="flex gap-2">
          <button onClick={submit} disabled={busy || pin.length < 4} data-testid="gov-stepup-submit" className="flex-1 bg-gold text-black font-mono text-[10px] font-black uppercase tracking-widest py-2.5 rounded-full border-2 border-charcoal disabled:opacity-50">{busy ? "Verifying…" : "Unlock"}</button>
          <button onClick={onClose} data-testid="gov-stepup-cancel" className="px-4 bg-white/10 text-white/60 border-2 border-cream/30 rounded-full font-mono text-[10px] uppercase">Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ============ C3 — 3-format media workspace (sink-mapped tabs) ========= */
export function MediaWorkspace() {
  const founder = useFounder();
  const [tab, setTab] = useState("video");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const inputRef = useRef(null);
  const reelRef = useRef(null);
  const [reel, setReel] = useState(null);
  const [reelBusy, setReelBusy] = useState(false);
  const TABS = {
    video: { label: "Walkthroughs", accept: "video/mp4", sink: "saveMp4ToVault", icon: FileText },
    brand: { label: "Logos / Images", accept: "image/*", sink: "saveBrandAssetToVault", icon: IdCard },
    music: { label: "Voiceovers", accept: "audio/*", sink: "saveMp3ToVault", icon: Activity },
  };

  useEffect(() => { if (founder) vaultSecurityApi.landingReel().then(setReel).catch(() => {}); }, [founder]);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const sink = window.MiStVaultSinks?.[TABS[tab].sink];
    if (!sink) { toast.error("Vault sink unavailable"); return; }
    setBusy(true);
    try { await sink(file); setLast(`${file.name} → ${TABS[tab].label}`); toast.success("Stored in vault"); }
    catch (e2) { toast.error(e2?.response?.data?.detail || "Store failed"); } finally { setBusy(false); }
  };

  const addReel = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setReelBusy(true);
    try { const r = await vaultSecurityApi.landingReelUpload(file); setReel(r); toast.success("Landing reel is live"); }
    catch (e2) { toast.error(e2?.response?.data?.detail || "Reel upload failed"); } finally { setReelBusy(false); }
  };
  const clearReel = async () => {
    setReelBusy(true);
    try { const r = await vaultSecurityApi.landingReelClear(); setReel(r); toast("Landing reel cleared"); }
    catch { toast.error("Clear failed"); } finally { setReelBusy(false); }
  };
  const generateReel = async () => {
    setReelBusy(true);
    try {
      await vaultSecurityApi.landingReelGenerate();
      toast.success("AI reel generating — it will auto-install when ready");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Generation unavailable");
    } finally { setReelBusy(false); }
  };

  return (
    <div className="mt-3 border-2 border-gold/40 rounded-xl bg-white/5 p-2.5" data-testid="media-workspace">
      <p className="font-mono text-[9px] uppercase tracking-widest text-gold/80 mb-1.5">Media Workspace</p>
      <div className="grid grid-cols-3 gap-1.5 mb-2">
        {Object.entries(TABS).map(([k, v]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`media-tab-${k}`}
            className={`flex flex-col items-center gap-1 rounded-lg border-2 py-1.5 font-mono text-[8px] font-black uppercase tracking-widest ${tab === k ? "bg-gold text-black border-charcoal" : "bg-white/5 text-white/50 border-gold/30"}`}>
            <v.icon size={13} /> {v.label}
          </button>
        ))}
      </div>
      <input ref={inputRef} type="file" hidden accept={TABS[tab].accept} onChange={onFile} data-testid="media-file-input" />
      <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="media-upload-btn"
        className="w-full flex items-center justify-center gap-1.5 border-2 border-dashed border-gold/50 rounded-lg py-2.5 font-mono text-[9px] font-black uppercase tracking-widest text-gold">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Ingest {TABS[tab].label}
      </button>
      {last && <p className="mt-1.5 font-mono text-[8px] text-white/50 truncate" data-testid="media-last">✓ {last}</p>}

      {/* Founder-only Landing Page Reel remote control */}
      {founder && (
        <div className="mt-3 border-2 border-rust/50 rounded-lg bg-rust/10 p-2.5" data-testid="reel-remote-control">
          <div className="flex items-center gap-1.5 mb-1.5">
            <p className="font-mono text-[9px] uppercase tracking-widest text-rust flex-1">Landing Reel Remote</p>
            <span className={`font-mono text-[8px] font-black uppercase px-1.5 py-0.5 rounded-full border ${reel?.active ? "bg-emerald-500 text-white border-charcoal" : "border-rust/50 text-rust"}`} data-testid="reel-status">{reel?.active ? "LIVE" : "OFF"}</span>
          </div>
          <input ref={reelRef} type="file" hidden accept="video/mp4" onChange={addReel} data-testid="reel-file-input" />
          <div className="grid grid-cols-2 gap-1.5">
            <button onClick={() => reelRef.current?.click()} disabled={reelBusy} data-testid="reel-add-btn" className="flex items-center justify-center gap-1 bg-gold text-black border-2 border-charcoal rounded-lg py-2 font-mono text-[8px] font-black uppercase tracking-widest">{reelBusy ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} Add New Reel</button>
            <button onClick={clearReel} disabled={reelBusy || !reel?.active} data-testid="reel-clear-btn" className="flex items-center justify-center gap-1 bg-red-600/80 text-white border-2 border-charcoal rounded-lg py-2 font-mono text-[8px] font-black uppercase tracking-widest disabled:opacity-40"><X size={11} strokeWidth={3} /> Remove Reel</button>
          </div>
          <p className="mt-1.5 font-mono text-[8px] text-white/50 leading-snug">Overrides the public Landing Page #1 showcase video source instantly.</p>
          <button onClick={generateReel} disabled={reelBusy} data-testid="reel-generate-btn"
            className="mt-1.5 w-full flex items-center justify-center gap-1.5 bg-charcoal text-gold border-2 border-gold rounded-lg py-2 font-mono text-[8px] font-black uppercase tracking-widest disabled:opacity-50">
            {reelBusy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} Generate with AI (Kling 9:16)
          </button>
        </div>
      )}
    </div>
  );
}

/* ============ C4 — Security override extras (Q&A + PIN mutator) ======== */
export function SecurityOverrideExtras() {
  const [q, setQ] = useState("First pet name?");
  const [a, setA] = useState("");
  const [pinForm, setPinForm] = useState({ current_pin: "", new_pin: "" });
  const [busy, setBusy] = useState(false);
  const QUESTIONS = ["First pet name?", "Model of first car?", "Name of your first school?"];

  const saveQA = async () => {
    if (a.trim().length < 1) { toast.error("Enter an answer"); return; }
    setBusy(true);
    try { await vaultSecurityApi.setSecurityQuestion(q, a.trim()); setA(""); toast.success("Challenge Q&A updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const changePin = async () => {
    if (!/^\d{4,12}$/.test(pinForm.new_pin)) { toast.error("New PIN 4–12 digits"); return; }
    setBusy(true);
    try { await vaultSecurityApi.pinChange(pinForm.current_pin, pinForm.new_pin); setPinForm({ current_pin: "", new_pin: "" }); toast.success("Master PIN updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <>
      {/* Challenge Phrase Q&A */}
      <div className="border-2 border-gold/50 rounded-2xl bg-white/5 p-3.5 shadow-[3px_3px_0px_#000]" data-testid="sec-card-qa">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-9 h-9 rounded-xl border-2 border-gold bg-white/10 grid place-items-center"><RefreshCw size={16} className="text-gold" /></span>
          <div className="flex-1 leading-tight">
            <p className="font-serif text-lg text-white">Challenge Phrase Q&A</p>
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Case-insensitive · hashed</p>
          </div>
        </div>
        <select value={q} onChange={(e) => setQ(e.target.value)} data-testid="sec-qa-question" className="w-full bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-2 font-mono text-[10px] text-white outline-none">
          {QUESTIONS.map((x) => <option key={x} value={x} className="bg-charcoal">{x}</option>)}
        </select>
        <input value={a} onChange={(e) => setA(e.target.value)} placeholder="Secret answer" data-testid="sec-qa-answer" className="mt-2 w-full bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-2 font-mono text-[10px] text-white outline-none focus:ring-2 focus:ring-gold" />
        <button onClick={saveQA} disabled={busy} data-testid="sec-qa-save" className="mt-2 w-full bg-gold text-black font-mono text-[9px] font-black uppercase tracking-widest py-2.5 rounded-full border-2 border-charcoal disabled:opacity-60">Save Q&A</button>
      </div>

      {/* Master PIN mutator */}
      <div className="border-2 border-gold/50 rounded-2xl bg-white/5 p-3.5 shadow-[3px_3px_0px_#000]" data-testid="sec-card-pinmut">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-9 h-9 rounded-xl border-2 border-gold bg-white/10 grid place-items-center"><KeyRound size={16} className="text-gold" /></span>
          <div className="flex-1 leading-tight">
            <p className="font-serif text-lg text-white">⚙️ Modify Master PIN</p>
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Validates current credential</p>
          </div>
        </div>
        <input type="password" value={pinForm.current_pin} onChange={(e) => setPinForm((s) => ({ ...s, current_pin: e.target.value }))} placeholder="Current PIN / passphrase" data-testid="sec-pin-current" className="w-full bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-2 font-mono text-[10px] text-white outline-none focus:ring-2 focus:ring-gold" />
        <input value={pinForm.new_pin} onChange={(e) => setPinForm((s) => ({ ...s, new_pin: e.target.value.replace(/\D/g, "").slice(0, 12) }))} inputMode="numeric" placeholder="New numeric PIN (4–12)" data-testid="sec-pin-new" className="mt-2 w-full bg-white/10 border-2 border-gold/60 rounded-lg px-2 py-2 font-mono text-[10px] tracking-[0.3em] text-white outline-none focus:ring-2 focus:ring-gold" />
        <button onClick={changePin} disabled={busy} data-testid="sec-pin-change" className="mt-2 w-full bg-gold text-black font-mono text-[9px] font-black uppercase tracking-widest py-2.5 rounded-full border-2 border-charcoal disabled:opacity-60">Update Master PIN</button>
      </div>
    </>
  );
}
